/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router';
import { Link as RouterLink, useParams } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  Typography,
  makeStyles,
  Button
} from '@material-ui/core';
import {
  Users as UserIcon,
  Grid as ListIcon,
  Key as KeyIcon
} from 'react-feather';
import Logo from 'src/components/Logo';
import NavItem from './NavItem';
import { useTranslation } from 'react-i18next';

const subheaderStyle = {
  letterSpacing: '1.3px'
};

function renderNavItems({ items, ...rest }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, ...rest }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth = 0 }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 54,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

function NavBar({ openMobile, onMobileClose }) {
  const { workspaceId } = useParams();
  const classes = useStyles();
  const location = useLocation();
  const { t, i18n } = useTranslation(['admin']);

  function changeToEnglish() {
    i18n.changeLanguage('en');
  }
  function changeToSpanish() {
    i18n.changeLanguage('es');
  }
  const navConfig = [
    {
      subheader: t('title'),
      href: `/admin/${workspaceId}/bots`,
      items: [
        {
          title: t('first'),
          href: `/admin/${workspaceId}/bots`,
          icon: ListIcon
        },
        {
          title: t('second'),
          href: `/admin/${workspaceId}/staff`,
          icon: UserIcon
        },
        {
          title: t('third'),
          href: `/admin/${workspaceId}/roles`,
          icon: KeyIcon
        }
      ]
    }
  ];
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <Box height='100%' display='flex' flexDirection='column'>
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display='flex' justifyContent='center'>
            <RouterLink to='/'>
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Divider />
        <Box p={2}>
          {navConfig.map(config => (
            <List
              style={subheaderStyle}
              key={config.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {config.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: config.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
        <Divider />
        <Box p={2}>
          <Box
            p={2}
            display='flex'
            flexDirection='column'
            borderRadius='borderRadius'
            bgcolor='background.dark'
          >
            <Typography variant='h6' color='textPrimary'>
              {t('HelpTitle')}
            </Typography>
            <Link
              variant='subtitle1'
              color='secondary'
              component={RouterLink}
              to='/docs'
            >
              {t('Link')}
            </Link>
            <div style={{ display: 'flex' }}>
              <Button onClick={changeToSpanish}>Español</Button>
              <Button onClick={changeToEnglish}>English</Button>
            </div>
          </Box>
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor='left'
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant='temporary'
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor='left'
          classes={{ paper: classes.desktopDrawer }}
          open
          variant='persistent'
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
